/**
 * Pricing seat-category helpers.
 *
 * Engine contract (rules.only_for_categories):
 * - absent → all seat categories
 * - `[]` → none
 * - `[...]` → membership
 */

/** @returns {string[]|undefined} Normalized categories, or undefined when absent (= all). */
export const pricing_categories = (pricing) => {
    if (!pricing || typeof pricing !== 'object')
        return undefined;

    if (!Array.isArray(pricing?.rules?.only_for_categories))
        return undefined;

    return pricing.rules.only_for_categories.filter(c => typeof c === 'string' && c !== '');
};

/**
 * Stable fingerprint for grouping pricings by categories set.
 * `__all__` = absent (all), `__none__` = explicit empty, otherwise sorted joined list.
 */
export const categories_fingerprint = (pricing) => {
    const cats = pricing_categories(pricing);
    if (cats === undefined)
        return '__all__';
    if (cats.length === 0)
        return '__none__';
    return [...cats].sort().join('\u0001');
};

/** Human-readable categories for tables; null when absent (= all / default display). */
export const categories_display_value = (pricing) => {
    const cats = pricing_categories(pricing);
    if (cats === undefined)
        return '';
    if (cats.length === 0)
        return '∅';
    return cats.join(', ');
};

/** True when categories column is worth showing (constrained / non-default). */
export const categories_is_notable = (pricing) => {
    const cats = pricing_categories(pricing);
    if (cats === undefined)
        return false;
    if (cats.length === 0)
        return true;
    return !(cats.length === 1 && cats[0] === 'default');
};

export const pricing_allows_category = (pricing, seat_category) => {
    if (!pricing || !seat_category)
        return false;

    const cats = pricing_categories(pricing);
    if (cats === undefined)
        return true;
    if (cats.length === 0)
        return false;
    return cats.includes(seat_category);
};

/**
 * Apply only_for_categories pledge filter before js-lib bucketMatch/assignBucket
 * (js-lib may not support the rule yet).
 */
export const apply_only_for_categories_pledge = (screening_obj, pledged_category) => {
    if (!screening_obj?.buckets)
        return screening_obj;

    const buckets = screening_obj.buckets.map((bucket) => {
        if (!bucket?.rules || !('only_for_categories' in bucket.rules))
            return bucket;

        if (pledged_category && Array.isArray(bucket.rules.only_for_categories)
            && bucket.rules.only_for_categories.includes(pledged_category)) {
            return bucket;
        }

        // Force non-match: present empty only_for_tickettypes never intersects.
        return {
            ...bucket,
            rules: {
                ...bucket.rules,
                only_for_tickettypes: [],
            },
        };
    });

    return { ...screening_obj, buckets };
};

/**
 * Pick a representative pledged seat category for a pricing group fingerprint.
 * Prefers a category that intersects contingent only_for_categories when present.
 */
export const pledge_category_for_fingerprint = (fingerprint, intersecting_categories = []) => {
    if (fingerprint === '__none__')
        return null;
    if (fingerprint === '__all__') {
        return intersecting_categories[0] || 'default';
    }
    const cats = fingerprint.split('\u0001').filter(Boolean);
    const hit = cats.find(c => intersecting_categories.includes(c));
    return hit || cats[0] || null;
};

/**
 * Group tickettype pricings by categories fingerprint.
 * @returns {{ fingerprint: string, categories: string[]|undefined, pricing_ids: string[], pricing_names: object[] }[]}
 */
export const group_pricings_by_categories = (pricings) => {
    const map = new Map();
    Object.entries(pricings || {}).forEach(([id, pricing]) => {
        if (!pricing)
            return;
        const fp = categories_fingerprint(pricing);
        if (!map.has(fp)) {
            map.set(fp, {
                fingerprint: fp,
                categories: pricing_categories(pricing),
                pricing_ids: [],
                pricing_names: [],
            });
        }
        const g = map.get(fp);
        g.pricing_ids.push(id);
        if (pricing.name)
            g.pricing_names.push(pricing.name);
    });
    return [...map.values()];
};

/** Seat categories present on a hall map (map / opaque seats / zones). */
export const hall_seat_categories = (hall) => {
    const seats = hall?.map?.seats || hall?.seats || hall?.opaque?.seats || [];
    const cats = new Set();
    (Array.isArray(seats) ? seats : []).forEach((seat) => {
        const c = seat?.category || seat?.zoneCat;
        if (typeof c === 'string' && c !== '')
            cats.add(c);
    });
    return [...cats];
};

/** Distinct seat categories across places, sorted. */
export const places_seat_categories = (places) => {
    const cats = new Set();
    (places || []).forEach((place) => {
        hall_seat_categories(place).forEach((c) => cats.add(c));
    });
    return [...cats].sort((a, b) => a.localeCompare(b));
};

/**
 * Whether choosing among distinct pricing category sets would change simulator results.
 */
export const pricing_categories_impact_simulator = ({
    groups,
    buckets,
    screening,
}) => {
    if (!groups || groups.length < 2)
        return false;

    const has_only_for_categories = (buckets || []).some(
        b => b?.rules && ('only_for_categories' in b.rules)
    );
    if (has_only_for_categories)
        return true;

    const map_only = !!(screening?.opaque?.map_only_bookings);
    if (!map_only)
        return false;

    const hall_cats = new Set(hall_seat_categories(screening?.cinema_hall));
    if (hall_cats.size === 0)
        return false;

    // Impactful if at least two groups constrain seats differently vs hall categories.
    const constrained = groups.filter((g) => {
        if (g.fingerprint === '__all__')
            return true;
        if (g.fingerprint === '__none__')
            return true;
        return (g.categories || []).some(c => hall_cats.has(c));
    });
    return constrained.length >= 2;
};
